import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const GLOBAL_CLOSE_EVENT = 'app:dropdown-close-all';

type Align = 'left' | 'right';

interface SimpleMenuProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  align?: Align; // alignment relative to trigger
  className?: string;
  menuClassName?: string;
  zIndexClassName?: string; // allow custom z-index classes
  position?: 'auto' | 'right-bottom';
  onOpenChange?: (open: boolean) => void; // callback when menu opens/closes
}

export function SimpleMenu({
  trigger,
  children,
  align = 'right',
  className = '',
  menuClassName = '',
  zIndexClassName = 'z-[2000]',
  position = 'auto',
  onOpenChange
}: SimpleMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const handleOutside = (ev: MouseEvent | TouchEvent) => {
      const target = ev.target as Node | null;
      if (
        open &&
        target &&
        !rootRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        close();
      }
    };
    const handleEsc = (ev: KeyboardEvent) => {
      if (open && ev.key === 'Escape') close();
    };
    const handleGlobalClose = () => close();

    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleEsc);
    window.addEventListener(GLOBAL_CLOSE_EVENT, handleGlobalClose);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleEsc);
      window.removeEventListener(GLOBAL_CLOSE_EVENT, handleGlobalClose);
    };
  }, [open, close]);

  const toggleOpen = useCallback((e?: Pick<React.SyntheticEvent, 'preventDefault' | 'stopPropagation'>) => {
    if (e) {
      // On touch devices, prevent synthetic click after touchend
      // Only preventDefault if the event is cancelable (not during scrolling)
      if ('preventDefault' in e && (e as any).cancelable !== false) {
        e.preventDefault();
      }
      if ('stopPropagation' in e) e.stopPropagation();
    }
    if (!open) window.dispatchEvent(new CustomEvent(GLOBAL_CLOSE_EVENT));
    setOpen(prev => {
      const newOpen = !prev;
      onOpenChange?.(newOpen);
      return newOpen;
    });
  }, [open, onOpenChange]);

  const triggerProps = useMemo(() => ({
    onClick: (e: React.MouseEvent) => toggleOpen(e),
    onTouchEnd: (e: React.TouchEvent) => toggleOpen(e),
    onPointerDown: (e: React.PointerEvent) => { e.stopPropagation(); },
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') toggleOpen(e);
    },
    'aria-haspopup': 'menu' as const,
    'aria-expanded': open,
    tabIndex: 0,
    role: 'button' as const,
  }), [open, toggleOpen]);

  // Position menu using fixed coordinates to escape stacking/overflow contexts
  const computePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    let top: number;
    let left: number;
    if (position === 'right-bottom') {
      // start to the right; vertical will be bottom-aligned after measuring menu
      top = rect.bottom;
      left = rect.right + 4;
    } else {
      // auto: open below trigger with small offset
      top = rect.bottom + 4;
      left = align === 'right' ? rect.right : rect.left;
    }
    setCoords({ top, left });
  }, [align, position]);

  useLayoutEffect(() => {
    if (open) {
      computePosition();
    }
  }, [open, computePosition]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => computePosition();
    const onScroll = () => computePosition();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, computePosition]);

  // (removed unused contentAlignClass)

  // After render, adjust position to prevent viewport overflow
  useLayoutEffect(() => {
    if (!open || !coords || !menuRef.current || !triggerRef.current) return;
    const menuBox = menuRef.current.getBoundingClientRect();
    const triggerBox = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    let newTop = coords.top;
    let newLeft = coords.left;

    if (position === 'right-bottom') {
      // Desired: open to the right, bottom-aligned with trigger (menu grows upward)
      // Position menu so its bottom aligns with trigger's bottom
      newTop = triggerBox.bottom - menuBox.height;
      newLeft = coords.left;

      // Clear any previous max-height/overflow constraints
      if (menuRef.current) {
        const innerDiv = menuRef.current.querySelector('div') as HTMLElement;
        if (innerDiv) {
          innerDiv.style.maxHeight = '';
          innerDiv.style.overflowY = '';
        }
      }
      
      // If positioning above would push menu above viewport, allow it to grow down instead
      if (newTop < margin) {
        // Position below trigger, letting it grow down naturally
        newTop = triggerBox.bottom + 4;
      }
      
      // Horizontal flip if overflowing right edge
      if (newLeft + menuBox.width > window.innerWidth - margin) {
        newLeft = triggerBox.left - 4 - menuBox.width;
      }
    } else {
      // auto mode: check if menu would overflow bottom of viewport
      const spaceBelow = window.innerHeight - triggerBox.bottom;
      const spaceAbove = triggerBox.top;
      const menuHeight = menuBox.height || 200; // Fallback if not measured yet

      // If menu height is 0 or very small, it might not be fully rendered yet
      // In that case, use a reasonable estimate
      if (menuHeight < 10) {
        // Menu not fully rendered, use estimated height
        const estimatedHeight = 200; // Reasonable estimate for a menu
        if (spaceBelow < estimatedHeight + margin && spaceAbove > estimatedHeight + margin) {
          newTop = triggerBox.top - estimatedHeight - 4;
        }
      } else {
        // Menu is measured, use actual height
        // If not enough space below but enough space above, flip to open upward
        if (spaceBelow < menuHeight + margin && spaceAbove > menuHeight + margin) {
          newTop = triggerBox.top - menuHeight - 4;
        } else if (spaceBelow < menuHeight + margin) {
          // Not enough space in either direction - constrain height and allow scrolling
          const maxHeight = Math.max(100, Math.min(spaceBelow - margin, menuHeight));
          if (menuRef.current) {
            const innerDiv = menuRef.current.querySelector('div') as HTMLElement;
            if (innerDiv) {
              innerDiv.style.maxHeight = `${maxHeight}px`;
              innerDiv.style.overflowY = 'auto';
            }
          }
        } else {
          // Clear any previous constraints when there's enough space
          if (menuRef.current) {
            const innerDiv = menuRef.current.querySelector('div') as HTMLElement;
            if (innerDiv) {
              innerDiv.style.maxHeight = '';
              innerDiv.style.overflowY = '';
            }
          }
        }
      }

      // Horizontal adjustment for align mode
      if (align === 'right') {
        newLeft = triggerBox.right - menuBox.width;
        if (newLeft < margin) {
          newLeft = margin;
        }
      } else {
        newLeft = triggerBox.left;
        if (newLeft + menuBox.width > window.innerWidth - margin) {
          newLeft = window.innerWidth - menuBox.width - margin;
        }
      }
    }
    
    if (newTop !== coords.top || newLeft !== coords.left) {
      setCoords({ top: newTop, left: newLeft });
    }
  }, [open, coords, position, align, children]); // Add align to deps

  const enhancedTrigger = useMemo(() => {
    if (React.isValidElement(trigger)) {
      const original: any = trigger;
      const mergedProps: any = {
        ...original.props,
        onClick: (e: any) => { toggleOpen(e); original.props?.onClick?.(e); },
        onTouchEnd: (e: any) => { 
          // Only preventDefault if the event is cancelable (not during scrolling)
          if (e.cancelable !== false) {
            e.preventDefault();
          }
          toggleOpen(e); 
          original.props?.onTouchEnd?.(e); 
        },
        onPointerDown: (e: any) => { e.stopPropagation(); original.props?.onPointerDown?.(e); },
        onKeyDown: (e: any) => { if (e.key === 'Enter' || e.key === ' ') toggleOpen(e); original.props?.onKeyDown?.(e); },
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        tabIndex: original.props?.tabIndex ?? 0,
        role: original.props?.role ?? 'button',
      };
      return React.cloneElement(original, mergedProps);
    }
    return (
      <div {...triggerProps}>
        {trigger}
      </div>
    );
  }, [trigger, open, toggleOpen, triggerProps]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      {/* Trigger (with both wrapper and child handlers) */}
      <div ref={triggerRef} {...triggerProps}>
        {enhancedTrigger}
      </div>

      {/* Menu in portal */}
      {open && coords && createPortal(
        <div
          ref={menuRef}
          className={`${zIndexClassName} fixed ${menuClassName}`}
          role="menu"
          style={{
            top: coords.top,
            left: coords.left,
            pointerEvents: 'auto',
            isolation: 'isolate',
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
        >
          <div 
            className="bg-popover text-popover-foreground border border-border rounded-lg shadow-md min-w-[12rem] py-1 animate-in fade-in-0 zoom-in-95 duration-100" 
            style={{ 
              pointerEvents: 'auto', 
              isolation: 'isolate'
            }}
          >
            {children}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

interface SimpleSubmenuProps {
  label: React.ReactNode;
  children: React.ReactNode;
}

export function SimpleSubmenu({ label, children }: SimpleSubmenuProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const submenuRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const onCloseAll = () => setOpen(false);
    window.addEventListener(GLOBAL_CLOSE_EVENT, onCloseAll);
    return () => window.removeEventListener(GLOBAL_CLOSE_EVENT, onCloseAll);
  }, []);

  const openMenu = (e?: React.SyntheticEvent) => {
    if (e) e.preventDefault();
    // Do NOT dispatch the global close here; this is a nested submenu
    setOpen(true);
  };
  const toggle = (e?: React.SyntheticEvent) => {
    if (e) e.preventDefault();
    if (!open) openMenu();
    else setOpen(false);
  };

  const compute = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    // default open to right
    let left = rect.right + 4;
    const top = rect.top;
    setCoords({ top, left });
    // after render, adjust if overflow
    requestAnimationFrame(() => {
      const subRect = submenuRef.current?.getBoundingClientRect();
      if (subRect) {
        const overflowRight = subRect.right > window.innerWidth;
        if (overflowRight) {
          left = rect.left - subRect.width - 4;
          setCoords({ top, left });
        }
      }
    });
  }, []);

  useLayoutEffect(() => {
    if (open) compute();
  }, [open, compute]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => compute();
    const onScroll = () => compute();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, compute]);

  return (
    <div ref={anchorRef} className="relative">
      <button
        className="w-full flex items-center px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground"
        onClick={toggle}
        onTouchEnd={(e) => { e.preventDefault(); toggle(e); }}
        role="menuitem"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label}
      </button>
      {open && coords && createPortal(
        <div
          ref={submenuRef}
          className="fixed z-[2100]"
          role="menu"
          style={{ top: coords.top, left: coords.left }}
          onMouseDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div className="bg-popover text-popover-foreground border border-border rounded-lg shadow-md min-w-[12rem] py-1 animate-in fade-in-0 zoom-in-95 duration-100">
            {children}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export function SimpleMenuItem({ children, onClick, disabled, className = '', closeOnClick = true, title }: { children: React.ReactNode; onClick?: () => void; disabled?: boolean; className?: string; closeOnClick?: boolean; title?: string }) {
  const handleAction = useCallback(() => {
    if (disabled) return;
    if (closeOnClick) window.dispatchEvent(new CustomEvent(GLOBAL_CLOSE_EVENT));
    onClick?.();
  }, [disabled, onClick, closeOnClick]);

  const touchHandledRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Use capture phase to ensure we handle events before Radix UI Sheet
  useEffect(() => {
    const button = buttonRef.current;
    if (!button || disabled) return;

    const handlePointerDown = (e: PointerEvent) => {
      // Only handle if the event is on this button or its children
      if (!button.contains(e.target as Node)) return;
      
      if (e.pointerType === 'touch') {
        touchHandledRef.current = false;
        e.stopPropagation();
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      // Only handle if the event is on this button or its children
      if (!button.contains(e.target as Node)) return;
      
      if (e.pointerType === 'touch') {
        // Only preventDefault if the event is cancelable (not during scrolling)
        if (e.cancelable !== false) {
          e.preventDefault();
        }
        e.stopPropagation();
        touchHandledRef.current = true;
        handleAction();
      }
    };

    // Use capture phase to intercept before Radix UI
    button.addEventListener('pointerdown', handlePointerDown, true);
    button.addEventListener('pointerup', handlePointerUp, true);

    return () => {
      button.removeEventListener('pointerdown', handlePointerDown, true);
      button.removeEventListener('pointerup', handlePointerUp, true);
    };
  }, [disabled, handleAction]);

  return (
    <button
      ref={buttonRef}
      className={`w-full flex items-center text-left px-3 py-2 text-sm rounded-sm ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-accent hover:text-accent-foreground'} ${className}`}
      style={{ 
        touchAction: 'manipulation', 
        pointerEvents: 'auto',
        WebkitTapHighlightColor: 'transparent'
      }}
      onClick={(e) => {
        if (disabled) return;
        // If touch was already handled, don't handle click to avoid double-firing
        if (touchHandledRef.current) {
          touchHandledRef.current = false;
          return;
        }
        e.stopPropagation();
        handleAction();
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
      }}
      onTouchEnd={(e) => {
        if (disabled) return;
        // Only handle if pointer events didn't already handle it
        if (!touchHandledRef.current) {
          // Only preventDefault if the event is cancelable (not during scrolling)
          if (e.cancelable !== false) {
            e.preventDefault();
          }
          e.stopPropagation();
          touchHandledRef.current = true;
          handleAction();
        }
      }}
      onPointerDown={(e) => {
        // Ensure pointer events work
        if (e.pointerType === 'touch') {
          e.stopPropagation();
        }
      }}
      onPointerUp={(e) => {
        if (disabled || e.pointerType !== 'touch') return;
        // Fallback for pointer events if touch events didn't fire
        if (!touchHandledRef.current && buttonRef.current?.contains(e.target as Node)) {
          // Only preventDefault if the event is cancelable (not during scrolling)
          if (e.cancelable !== false) {
            e.preventDefault();
          }
          e.stopPropagation();
          touchHandledRef.current = true;
          handleAction();
        }
      }}
      role="menuitem"
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}


